// custom-text-filter.component.ts
import { Component, HostListener, ViewEncapsulation  } from '@angular/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

interface FilterCondition {
    type: string;
    value: string;
}

@Component({
    selector: 'app-custom-text-filter',
    templateUrl: './custom-text-filter.component.html',
    styleUrls: ['./custom-text-filter.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CustomTextFilterComponent implements IFilterAngularComp {
    private params!: IFilterParams;
    public filterConditions: FilterCondition[] = [
        { type: 'contains', value: '' },
        { type: 'contains', value: '' }
    ];
    public junction: 'AND' | 'OR' = 'AND';
    public dropdownOpenIndex: number | null = null;
    private caseSensitive = true;

    filterOptions = [
        { value: 'contains', label: 'Contains' },
        { value: 'notContains', label: 'Does not contain' },
        { value: 'equals', label: 'Equals' },
        { value: 'notEqual', label: 'Does not equal' },
        { value: 'startsWith', label: 'Begins with' },
        { value: 'endsWith', label: 'Ends with' },
        { value: 'blank', label: 'Blank' },
        { value: 'notBlank', label: 'Not Blank' }
    ];

    agInit(params: IFilterParams): void {
        this.params = params;
        const instanceFilterParams = (params as any).filterParams as { caseSensitive?: boolean } | undefined;
        this.caseSensitive = instanceFilterParams?.caseSensitive ?? true;
    }

    isBlankType(type: string): boolean {
        return type === 'blank' || type === 'notBlank';
    }

    isFirstConditionSet(): boolean {
        return this.isBlankType(this.filterConditions[0].type) ||
            this.filterConditions[0].value !== '';
    }

    onTypeChange(index: number): void {
        if (this.isBlankType(this.filterConditions[index].type)) {
            this.filterConditions[index].value = 'BLANK'; // Special value for blank conditions
        } else {
            this.filterConditions[index].value = '';
        }
        this.onFilterChanged();
    }

    isFilterActive(): boolean {
        return this.filterConditions.some(condition =>
            this.isBlankType(condition.type) || condition.value !== ''
        );
    }

    doesFilterPass(params: IDoesFilterPassParams): boolean {
        const value = params.data[this.params.colDef.field]?.toString() || '';

        const condition1Pass = this.checkCondition(this.filterConditions[0], value);

        // If there's no second condition, return first condition result
        if (!this.isFirstConditionSet()) {
            return condition1Pass;
        }

        const condition2Pass = this.checkCondition(this.filterConditions[1], value);

        return this.junction === 'AND'
            ? condition1Pass && condition2Pass
            : condition1Pass || condition2Pass;
    }

    private checkCondition(condition: FilterCondition, value: string): boolean {
        if (condition.type === 'blank') {
            return !value || value.trim() === '';
        }
        if (condition.type === 'notBlank') {
            return value.trim() !== '';
        }

        const normalizedValue = this.normalizeValue(value);
        const normalizedFilter = this.normalizeValue(condition.value);

        switch (condition.type) {
            case 'contains':
                return normalizedValue.includes(normalizedFilter);
            case 'notContains':
                return !normalizedValue.includes(normalizedFilter);
            case 'equals':
                return normalizedValue === normalizedFilter;
            case 'notEqual':
                return normalizedValue !== normalizedFilter;
            case 'startsWith':
                return normalizedValue.startsWith(normalizedFilter);
            case 'endsWith':
                return normalizedValue.endsWith(normalizedFilter);
            default:
                return true;
        }
    }

    private normalizeValue(text: string): string {
        return this.caseSensitive ? text : text.toLowerCase();
    }

    getModel() {
        return {
            conditions: this.filterConditions,
            junction: this.junction
        };
    }

    setModel(model: any): void {
        if (model) {
            if (model.conditions === undefined) {
                // Get all condition keys (condition1, condition2, etc.)
                const conditionKeys = Object.keys(model).filter(key => key.startsWith('condition'));
                if (conditionKeys.length > 0) {
                    const conditions = [];
                    // Process each condition
                    conditionKeys.forEach(key => {
                        const condition = model[key];
                        if (condition && condition.type && condition.filter) {
                            conditions.push({
                                type: condition.type,
                                value: condition.filter
                            });
                        }
                    });
                    if (conditions.length === 0) {
                        this.resetFilter();
                        return;
                    }
                    this.filterConditions = conditions;
                    this.junction = model.operator;
                } else if (model.type && model.filter) {
                    this.filterConditions[0] = { type: model.type, value: model.filter };
                } else {
                    this.resetFilter();
                }
            } else {
                this.filterConditions = model.conditions;
                this.junction = model.junction;
            }
        } else {
            this.resetFilter();
        }
    }

    resetFilter(): void {
        this.filterConditions = [
            { type: 'contains', value: '' },
            { type: 'contains', value: '' }
        ];
        this.junction = 'AND';
        this.onFilterChanged();
    }

    onFilterChanged(): void {
        this.params.filterChangedCallback();
    }

    getOptionLabel(value: string): string {
        const match = this.filterOptions.find(o => o.value === value);
        return match ? match.label : this.filterOptions[0].label;
    }

    toggleDropdown(index: number): void {
        this.dropdownOpenIndex = this.dropdownOpenIndex === index ? null : index;
    }

    selectOption(index: number, optionValue: string): void {
        this.filterConditions[index].type = optionValue;
        this.onTypeChange(index);   // keep your existing logic
        this.dropdownOpenIndex = null;
    }



    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.select-wrapper')) {
            this.dropdownOpenIndex = null;
        }
    }

}
